import { type ReactNode, useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { PublicHeader } from '@/components/PublicHeader';
import { Faq } from './GuideKit';
import { FAQ_COMPETITOR, FAQ_JUDGE, FAQ_ORGANIZER, FAQ_START } from './faq';
import { Competitor, GettingStarted, Judge, Lifecycle, Organizer, Overview, Roles, Rules, Visitor } from './sections';

/*
 * Navodila za uporabo - the user guide of the whole platform, public, at /navodila-za-uporabo and one page per
 * audience below it. Every audience page ends with its own frequently asked questions (faq.ts).
 *
 * KEEP IN SYNC: whenever a page, a form, a rule or a role's permissions change, update the matching section here
 * (see CLAUDE.md). Screenshots are in frontend/public/guide.
 */

interface GuideSection {
  id: string;
  title: string;
  render: () => ReactNode;
}

interface GuidePage {
  slug: string;
  title: string;
  summary: string;
  sections: GuideSection[];
}

const PAGES: GuidePage[] = [
  {
    slug: '',
    title: 'Pregled',
    summary: 'Kaj je Zaslonka, kdo kaj lahko in kako poteka tekmovanje.',
    sections: [
      { id: 'pregled', title: 'Kaj je Zaslonka', render: Overview },
      { id: 'vloge', title: 'Vloge', render: Roles },
      { id: 'zivljenjski-krog', title: 'Življenjski krog tekmovanja', render: Lifecycle },
      { id: 'pravila', title: 'Pravila, zasebnost in pomoč', render: Rules },
    ],
  },
  {
    slug: 'zacetek',
    title: 'Prvi koraki',
    summary: 'Račun, prijava, javni razpis in prijava na tekmovanje.',
    sections: [
      { id: 'zacetek', title: 'Prvi koraki in račun', render: GettingStarted },
      { id: 'obiskovalec', title: 'Razpis in prijava na tekmovanje', render: Visitor },
      { id: 'pogosta-vprasanja', title: 'Pogosta vprašanja', render: () => <Faq items={FAQ_START} /> },
    ],
  },
  {
    slug: 'tekmovalec',
    title: 'Tekmovalec',
    summary: 'Oddaja fotografij, zahteve za slike in rezultati.',
    sections: [
      { id: 'tekmovalec', title: 'Oddaja in rezultati', render: Competitor },
      { id: 'pogosta-vprasanja', title: 'Pogosta vprašanja tekmovalcev', render: () => <Faq items={FAQ_COMPETITOR} /> },
    ],
  },
  {
    slug: 'sodnik',
    title: 'Sodnik',
    summary: 'Ocenjevanje prispevkov in razvrstitev.',
    sections: [
      { id: 'sodnik', title: 'Ocenjevanje in razvrstitev', render: Judge },
      { id: 'pogosta-vprasanja', title: 'Pogosta vprašanja sodnikov', render: () => <Faq items={FAQ_JUDGE} /> },
    ],
  },
  {
    slug: 'organizator',
    title: 'Organizator',
    summary: 'Priprava razpisa, pregled prispevkov, sodniki, nagrade in poročila.',
    sections: [
      { id: 'organizator', title: 'Vodenje tekmovanja', render: Organizer },
      { id: 'pogosta-vprasanja', title: 'Pogosta vprašanja organizatorjev', render: () => <Faq items={FAQ_ORGANIZER} /> },
    ],
  },
];

const BASE = '/navodila-za-uporabo';
const pagePath = (slug: string) => (slug ? `${BASE}/${slug}` : BASE);

export function UserGuidePage() {
  const { page = '' } = useParams<{ page: string }>();
  const current = PAGES.find((p) => p.slug === page);
  useEffect(() => {
    if (current) document.title = `${current.title} | Navodila za uporabo | Zaslonka`;
  }, [current]);
  if (!current) return <Navigate to={BASE} replace />;

  const index = PAGES.indexOf(current);
  const previous = PAGES[index - 1];
  const next = PAGES[index + 1];

  return (
    <div className="min-h-screen bg-surface-0 text-fg">
      <PublicHeader />
      <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-8 md:flex-row md:gap-8">
        <aside className="shrink-0 md:sticky md:top-6 md:h-fit md:w-56">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-fg-muted">Navodila</p>
          <nav className="flex flex-wrap gap-1 md:flex-col md:gap-0.5">
            {PAGES.map((p) => (
              <Link
                key={p.slug}
                to={pagePath(p.slug)}
                aria-current={p === current ? 'page' : undefined}
                className={`rounded-control px-2.5 py-1.5 text-sm transition-colors duration-fast ${
                  p === current ? 'bg-surface-3 font-medium text-fg' : 'text-fg-secondary hover:text-fg'
                }`}
              >
                {p.title}
              </Link>
            ))}
          </nav>
        </aside>

        <main className="flex min-w-0 flex-1 flex-col gap-8">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight text-fg">
              {current.slug ? current.title : 'Navodila za uporabo'}
            </h1>
            <p className="mt-1 text-sm text-fg-muted">{current.summary}</p>
            {current.sections.length > 2 && (
              <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {current.sections.map((s) => (
                  <a key={s.id} href={`#${s.id}`} className="text-accent-text hover:underline">
                    {s.title}
                  </a>
                ))}
              </p>
            )}
          </div>

          {!current.slug && (
            <div className="grid gap-3 sm:grid-cols-2">
              {PAGES.slice(1).map((p) => (
                <Link
                  key={p.slug}
                  to={pagePath(p.slug)}
                  className="rounded-card border border-line bg-surface-1 p-4 transition-colors duration-fast hover:bg-surface-2"
                >
                  <span className="font-medium text-fg">{p.title}</span>
                  <span className="mt-1 block text-sm text-fg-muted">{p.summary}</span>
                </Link>
              ))}
            </div>
          )}

          {current.sections.map((s) => (
            <section key={s.id} id={s.id} className="flex scroll-mt-6 flex-col gap-3">
              <h2 className="border-b border-line pb-2 text-xl font-semibold text-fg">{s.title}</h2>
              {s.render()}
            </section>
          ))}

          <nav className="flex justify-between gap-3 border-t border-line pt-4 text-sm">
            {previous ? (
              <Link to={pagePath(previous.slug)} className="text-accent-text hover:underline">
                ← {previous.title}
              </Link>
            ) : (
              <span />
            )}
            {next && (
              <Link to={pagePath(next.slug)} className="text-accent-text hover:underline">
                {next.title} →
              </Link>
            )}
          </nav>
        </main>
      </div>
    </div>
  );
}
